<!-- ── CASHFLOW STEP 3 — Rental Income ── -->
<app-ip-shell [activeStep]="3" [steps]="cfSteps" calculatorName="Cash Flow Calculator">

  <!-- ── FORM COLUMN ── -->
  <div formCol>
    <div class="ip-form-head">
      <div class="ip-form-head__step-meta">Step 03 &middot; Rental Income</div>
      <h1 class="ip-form-head__title">Rental income &amp; investment horizon</h1>
      <p class="ip-form-head__subtitle">
        Define your investment period, expected yield, and rental growth. These drive the year-by-year cash flow model.
      </p>
    </div>

    <div class="ip-form-body">

      <!-- Investment period -->
      <div class="ip-field">
        <label class="ip-field__label">Investment period</label>
        <div class="ip-chip-row">
          <button class="ip-chip" [class.is-active]="investedTenure==='3'"  (click)="investedTenure='3';  updatePreview()" type="button">3 yrs</button>
          <button class="ip-chip" [class.is-active]="investedTenure==='5'"  (click)="investedTenure='5';  updatePreview()" type="button">5 yrs</button>
          <button class="ip-chip" [class.is-active]="investedTenure==='10'" (click)="investedTenure='10'; updatePreview()" type="button">10 yrs</button>
          <button class="ip-chip" [class.is-active]="investedTenure==='15'" (click)="investedTenure='15'; updatePreview()" type="button">15 yrs</button>
          <button class="ip-chip" [class.is-active]="investedTenure==='20'" (click)="investedTenure='20'; updatePreview()" type="button">20 yrs</button>
          <button class="ip-chip" [class.is-active]="investedTenure==='25'" (click)="investedTenure='25'; updatePreview()" type="button">25 yrs</button>
        </div>
      </div>

      <!-- Gross Rental Yield -->
      <div class="ip-field">
        <div class="ip-field__head">
          <label class="ip-field__label" for="rentalYeild">Gross rental yield</label>
          <span class="ip-hint"><span class="ip-hint__dot"></span>UK avg: 5–7%</span>
        </div>
        <div class="ip-input">
          <input
            type="text"
            id="rentalYeild"
            class="ip-input__field"
            autocomplete="off"
            placeholder="e.g. 6"
            [(ngModel)]="rentalYeild"
            (keyup)="rentalYeild=validation.appendPercent($event,rentalYeild); updatePreview()"
            (keypress)="validation.DesimalOnly($event,rentalYeild)">
          <span class="ip-input__suffix">p.a.</span>
        </div>
      </div>

      <!-- Rental Growth -->
      <div class="ip-field">
        <div class="ip-field__head">
          <label class="ip-field__label" for="rentalGrowthEscalation">Rental escalation p.a.</label>
          <span class="ip-hint"><span class="ip-hint__dot"></span>UK avg: 2–4%</span>
        </div>
        <div class="ip-input">
          <input
            type="text"
            id="rentalGrowthEscalation"
            class="ip-input__field"
            autocomplete="off"
            placeholder="e.g. 2.5"
            [(ngModel)]="rentalGrowthEscalation"
            (keyup)="rentalGrowthEscalation=validation.appendPercent($event,rentalGrowthEscalation); updatePreview()"
            (keypress)="validation.DesimalOnly($event,rentalGrowthEscalation)">
          <span class="ip-input__suffix">p.a.</span>
        </div>
      </div>

    </div><!-- /ip-form-body -->

    <div class="ip-form-actions">
      <button class="ip-btn ip-btn--ghost" type="button" [routerLink]="['/cashflow/step2']">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline>
        </svg>
        Back
      </button>
      <button class="ip-btn ip-btn--primary" type="button" (click)="next()">
        Continue
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>
    </div>
  </div><!-- /formCol -->

  <!-- ── RAIL ── -->
  <div rail>
    <div class="ip-rail-summary">
      <div class="ip-rail-summary__head">
        <div class="ip-rail-summary__loc">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle>
          </svg>
          <span class="ip-rail-summary__loc-name">{{ calcData?.City || 'United Kingdom' }}</span>
        </div>
        <div class="ip-rail-summary__buyer-tag" [class.is-uk]="calcData?.ForeignBuyer!=='1'">
          {{ calcData?.ForeignBuyer === '1' ? 'Foreign Buyer' : 'UK Resident' }}
        </div>
      </div>

      <div class="ip-rail-summary__body">
        <!-- Property -->
        <div class="ip-rs-section">
          <div class="ip-rs-section__head">
            <span class="ip-rs-section__label">Property &amp; Financing</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Property value</span>
            <span class="ip-rs-row__val">£{{ calcData?.PropertyValue | number }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="calcData?.optmortgage==='1'">
            <span class="ip-rs-row__key">LTV · Type</span>
            <span class="ip-rs-row__val">{{ calcData?.loanAmount }}% · {{ calcData?.mortgageType === '1' ? 'Int Only' : 'Repayment' }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="calcData?.optmortgage==='0'">
            <span class="ip-rs-row__key">Financing</span>
            <span class="ip-rs-row__val">All-cash</span>
          </div>
        </div>

        <!-- Rental income (current step) -->
        <div class="ip-rs-section">
          <div class="ip-rs-section__head">
            <span class="ip-rs-section__label">Rental Income</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Period</span>
            <span class="ip-rs-row__val">{{ investedTenure }} years</span>
          </div>
          <div class="ip-rs-row" *ngIf="rentalYeild">
            <span class="ip-rs-row__key">Gross yield</span>
            <span class="ip-rs-row__val">{{ rentalYeild }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="rentalGrowthEscalation">
            <span class="ip-rs-row__key">Escalation</span>
            <span class="ip-rs-row__val">{{ rentalGrowthEscalation }}</span>
          </div>
        </div>

        <!-- Live preview -->
        <div class="ip-rs-section">
          <div class="ip-rs-section__head">
            <span class="ip-rs-section__label">Preview</span>
            <span class="ip-rs-section__total">£{{ grossAnnual | number:'1.0-0' }}/yr</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Gross annual</span>
            <span class="ip-rs-row__val">£{{ grossAnnual | number:'1.0-0' }}</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Est. net annual</span>
            <span class="ip-rs-row__val">£{{ netAnnual | number:'1.0-0' }}</span>
          </div>
          <div class="ip-rs-row">
            <span class="ip-rs-row__key">Est. net monthly</span>
            <span class="ip-rs-row__val">£{{ (netAnnual / 12) | number:'1.0-0' }}</span>
          </div>
          <div class="ip-rs-row" *ngIf="grossAnnual > 0 && propertyValue > 0">
            <span class="ip-rs-row__key">Gross yield</span>
            <span class="ip-rs-row__val">{{ (grossAnnual / propertyValue) * 100 | number:'1.1-1' }}%</span>
          </div>
        </div>
      </div>

      <div class="ip-rail-summary__outlay">
        <div class="ip-rail-summary__outlay-label">Costs &amp; SDLT</div>
        <div class="ip-rail-summary__outlay-value">Step 4</div>
        <div class="ip-rail-summary__outlay-meta">Fees and stamp duty added next</div>
      </div>
    </div>
  </div><!-- /rail -->

</app-ip-shell>
